/**
 * Unit of Measure Codes according to UN/CEFACT Recommendation 20
 * Source: UNTDID 2379 - Unit of measure code
 * Used in XRechnung, ZUGFeRD, and other electronic invoice formats
 */

export interface UnitCodeInfo {
  code: string;              // Official unit code
  name: string;              // English name
  germanName: string;        // German name
  symbol?: string;           // Common symbol
  description: string;       // Detailed description
  category: string;          // Category (quantity, weight, time, etc.)
  isCommon: boolean;         // Commonly used in invoicing
  isActive: boolean;         // Currently active
  examples: string[];        // Example use cases
}

export const UNIT_CODE_DATA: Record<string, UnitCodeInfo> = {
  // Most Common Units
  C62: {
    code: 'C62',
    name: 'Piece',
    germanName: 'Stück',
    symbol: 'pc',
    description: 'A unit of count defining the number of pieces',
    category: 'Quantity',
    isCommon: true,
    isActive: true,
    examples: ['Items', 'Products', 'Components', 'Units']
  },
  
  // Length Units
  MTR: {
    code: 'MTR',
    name: 'Metre',
    germanName: 'Meter',
    symbol: 'm',
    description: 'A unit of length equal to one metre',
    category: 'Length',
    isCommon: true,
    isActive: true,
    examples: ['Cables', 'Pipes', 'Fabric', 'Construction materials']
  },
  
  CMT: {
    code: 'CMT',
    name: 'Centimetre',
    germanName: 'Zentimeter',
    symbol: 'cm',
    description: 'A unit of length equal to one centimetre',
    category: 'Length',
    isCommon: true,
    isActive: true,
    examples: ['Small measurements', 'Dimensions']
  },
  
  MMT: {
    code: 'MMT',
    name: 'Millimetre',
    germanName: 'Millimeter',
    symbol: 'mm',
    description: 'A unit of length equal to one millimetre',
    category: 'Length',
    isCommon: true,
    isActive: true,
    examples: ['Precision measurements', 'Technical drawings']
  },
  
  KTM: {
    code: 'KTM',
    name: 'Kilometre',
    germanName: 'Kilometer',
    symbol: 'km',
    description: 'A unit of length equal to one kilometre',
    category: 'Length',
    isCommon: true,
    isActive: true,
    examples: ['Distance', 'Transport', 'Logistics']
  },
  
  // Area Units
  MTK: {
    code: 'MTK',
    name: 'Square metre',
    germanName: 'Quadratmeter',
    symbol: 'm²',
    description: 'A unit of area equal to one square metre',
    category: 'Area',
    isCommon: true,
    isActive: true,
    examples: ['Floor space', 'Land area', 'Construction']
  },
  
  // Volume Units
  MTQ: {
    code: 'MTQ',
    name: 'Cubic metre',
    germanName: 'Kubikmeter',
    symbol: 'm³',
    description: 'A unit of volume equal to one cubic metre',
    category: 'Volume',
    isCommon: true,
    isActive: true,
    examples: ['Gas', 'Concrete', 'Storage space']
  },
  
  LTR: {
    code: 'LTR',
    name: 'Litre',
    germanName: 'Liter',
    symbol: 'L',
    description: 'A unit of volume equal to one litre',
    category: 'Volume',
    isCommon: true,
    isActive: true,
    examples: ['Beverages', 'Fuel', 'Chemicals', 'Liquids']
  },
  
  // Weight Units
  KGM: {
    code: 'KGM',
    name: 'Kilogram',
    germanName: 'Kilogramm',
    symbol: 'kg',
    description: 'A unit of mass equal to one kilogram',
    category: 'Weight',
    isCommon: true,
    isActive: true,
    examples: ['Food products', 'Raw materials', 'Goods']
  },
  
  GRM: {
    code: 'GRM',
    name: 'Gram',
    germanName: 'Gramm',
    symbol: 'g',
    description: 'A unit of mass equal to one gram',
    category: 'Weight',
    isCommon: true,
    isActive: true,
    examples: ['Small items', 'Precious metals', 'Pharmaceuticals']
  },
  
  TNE: {
    code: 'TNE',
    name: 'Tonne',
    germanName: 'Tonne',
    symbol: 't',
    description: 'A unit of mass equal to one tonne (1000 kg)',
    category: 'Weight',
    isCommon: true,
    isActive: true,
    examples: ['Heavy materials', 'Bulk goods', 'Construction']
  },
  
  // Time Units
  HUR: {
    code: 'HUR',
    name: 'Hour',
    germanName: 'Stunde',
    symbol: 'h',
    description: 'A unit of time equal to one hour',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Labor', 'Consulting', 'Services', 'Rental']
  },
  
  MIN: {
    code: 'MIN',
    name: 'Minute',
    germanName: 'Minute',
    symbol: 'min',
    description: 'A unit of time equal to one minute',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Short services', 'Telecommunications']
  },
  
  DAY: {
    code: 'DAY',
    name: 'Day',
    germanName: 'Tag',
    symbol: 'd',
    description: 'A unit of time equal to one day',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Rental', 'Subscription', 'Daily services']
  },
  
  WEE: {
    code: 'WEE',
    name: 'Week',
    germanName: 'Woche',
    symbol: 'wk',
    description: 'A unit of time equal to one week',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Weekly services', 'Short-term contracts']
  },
  
  MON: {
    code: 'MON',
    name: 'Month',
    germanName: 'Monat',
    symbol: 'mo',
    description: 'A unit of time equal to one month',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Monthly services', 'Subscriptions', 'Rental']
  },
  
  ANN: {
    code: 'ANN',
    name: 'Year',
    germanName: 'Jahr',
    symbol: 'a',
    description: 'A unit of time equal to one year',
    category: 'Time',
    isCommon: true,
    isActive: true,
    examples: ['Annual services', 'Licenses', 'Warranties']
  },
  
  // Energy Units
  KWH: {
    code: 'KWH',
    name: 'Kilowatt hour',
    germanName: 'Kilowattstunde',
    symbol: 'kWh',
    description: 'A unit of energy equal to one kilowatt hour',
    category: 'Energy',
    isCommon: true,
    isActive: true,
    examples: ['Electricity', 'Power consumption']
  },
  
  MWH: {
    code: 'MWH',
    name: 'Megawatt hour',
    germanName: 'Megawattstunde',
    symbol: 'MWh',
    description: 'A unit of energy equal to one megawatt hour',
    category: 'Energy',
    isCommon: false,
    isActive: true,
    examples: ['Large-scale electricity', 'Industrial power']
  },
  
  // Digital Units
  BYT: {
    code: 'BYT',
    name: 'Byte',
    germanName: 'Byte',
    symbol: 'B',
    description: 'A unit of digital information equal to one byte',
    category: 'Digital',
    isCommon: false,
    isActive: true,
    examples: ['Data storage', 'File size']
  },
  
  // Special Units
  SET: {
    code: 'SET',
    name: 'Set',
    germanName: 'Satz',
    description: 'A unit of count defining the number of sets',
    category: 'Quantity',
    isCommon: true,
    isActive: true,
    examples: ['Product sets', 'Bundled items', 'Collections']
  },
  
  PAR: {
    code: 'PAR',
    name: 'Pair',
    germanName: 'Paar',
    description: 'A unit of count defining the number of pairs',
    category: 'Quantity',
    isCommon: true,
    isActive: true,
    examples: ['Shoes', 'Gloves', 'Paired items']
  },
  
  DZN: {
    code: 'DZN',
    name: 'Dozen',
    germanName: 'Dutzend',
    description: 'A unit of count defining the number of dozens (12 pieces)',
    category: 'Quantity',
    isCommon: true,
    isActive: true,
    examples: ['Eggs', 'Bottles', 'Small items']
  },
  
  // Percentage
  P1: {
    code: 'P1',
    name: 'Percent',
    germanName: 'Prozent',
    symbol: '%',
    description: 'A unit of proportion equal to one percent',
    category: 'Proportion',
    isCommon: true,
    isActive: true,
    examples: ['Discounts', 'Rates', 'Proportions']
  },
  
  // Box/Package Units
  BX: {
    code: 'BX',
    name: 'Box',
    germanName: 'Schachtel',
    description: 'A unit of count defining the number of boxes',
    category: 'Packaging',
    isCommon: true,
    isActive: true,
    examples: ['Packaged goods', 'Shipping boxes']
  },
  
  // Service Units
  E48: {
    code: 'E48',
    name: 'Service unit',
    germanName: 'Serviceeinheit',
    description: 'A unit of count defining the number of service units',
    category: 'Service',
    isCommon: true,
    isActive: true,
    examples: ['IT services', 'Consulting units', 'Service packages']
  },
  
  // Additional Length Units
  FOT: {
    code: 'FOT',
    name: 'Foot',
    germanName: 'Fuß',
    symbol: 'ft',
    description: 'A unit of length equal to one foot',
    category: 'Length',
    isCommon: false,
    isActive: true,
    examples: ['Imperial measurements', 'Construction (US)']
  },
  
  INH: {
    code: 'INH',
    name: 'Inch',
    germanName: 'Zoll',
    symbol: 'in',
    description: 'A unit of length equal to one inch',
    category: 'Length',
    isCommon: false,
    isActive: true,
    examples: ['Screen sizes', 'Pipe dimensions', 'Imperial measurements']
  }
};

// Common unit codes for prioritized display
export const COMMON_UNIT_CODES = ['C62', 'KGM', 'LTR', 'MTR', 'MTK', 'MTQ', 'HUR', 'SET', 'PAR', 'DZN'] as const;

// Unit categories for organized display
export const UNIT_CATEGORIES = {
  'Quantity': ['C62', 'SET', 'PAR', 'DZN', 'BX'],
  'Length': ['MTR', 'CMT', 'MMT', 'KTM', 'FOT', 'INH'],
  'Area': ['MTK'],
  'Volume': ['MTQ', 'LTR'],
  'Weight': ['KGM', 'GRM', 'TNE'],
  'Time': ['HUR', 'MIN', 'DAY', 'WEE', 'MON', 'ANN'],
  'Energy': ['KWH', 'MWH'],
  'Digital': ['BYT'],
  'Service': ['E48'],
  'Packaging': ['BX'],
  'Proportion': ['P1']
} as const;

// Get all active unit codes
export const getAllUnitCodes = (): string[] => {
  return Object.keys(UNIT_CODE_DATA).filter(code => UNIT_CODE_DATA[code].isActive);
};

// Get unit code info by code
export const getUnitCodeInfo = (code: string): UnitCodeInfo | undefined => {
  return UNIT_CODE_DATA[code.toUpperCase()];
};

// Get unit name by code
export const getUnitCodeName = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  return unit?.name || code;
};

// Get German unit name by code
export const getUnitCodeGermanName = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  return unit?.germanName || code;
};

// Get unit symbol by code
export const getUnitCodeSymbol = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  return unit?.symbol || code;
};

// Get formatted unit display (code + name)
export const getFormattedUnitDisplay = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  return unit ? `${code} - ${unit.name}` : code;
};

// Get formatted unit display with German name
export const getFormattedUnitDisplayGerman = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  return unit ? `${code} - ${unit.germanName}` : code;
};

// Get formatted unit display with symbol
export const getFormattedUnitDisplayWithSymbol = (code: string): string => {
  const unit = getUnitCodeInfo(code);
  if (!unit) return code;
  
  const symbol = unit.symbol ? ` (${unit.symbol})` : '';
  return `${code} - ${unit.name}${symbol}`;
};

// Get units by category
export const getUnitsByCategory = (category: keyof typeof UNIT_CATEGORIES): UnitCodeInfo[] => {
  const unitCodes = UNIT_CATEGORIES[category];
  return unitCodes.map(code => UNIT_CODE_DATA[code]).filter(Boolean);
};

// Search unit codes by name, German name, or code
export const searchUnitCodes = (query: string): UnitCodeInfo[] => {
  const searchTerm = query.toLowerCase();
  return Object.values(UNIT_CODE_DATA)
    .filter(unit => 
      unit.isActive && (
        unit.name.toLowerCase().includes(searchTerm) ||
        unit.germanName.toLowerCase().includes(searchTerm) ||
        unit.code.toLowerCase().includes(searchTerm) ||
        unit.symbol?.toLowerCase().includes(searchTerm) ||
        unit.description.toLowerCase().includes(searchTerm)
      )
    )
    .sort((a, b) => {
      // Prioritize exact matches
      const aExact = a.code.toLowerCase() === searchTerm;
      const bExact = b.code.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize common units
      const aCommon = a.isCommon;
      const bCommon = b.isCommon;
      
      if (aCommon && !bCommon) return -1;
      if (!aCommon && bCommon) return 1;
      
      // Finally alphabetical
      return a.name.localeCompare(b.name);
    });
};

// Get suggested unit codes for common categories
export const getSuggestedUnitCodes = (category?: string): string[] => {
  if (!category) return COMMON_UNIT_CODES.slice();
  
  const categoryKey = category as keyof typeof UNIT_CATEGORIES;
  if (UNIT_CATEGORIES[categoryKey]) {
    return UNIT_CATEGORIES[categoryKey].slice();
  }
  
  return COMMON_UNIT_CODES.slice();
};

// Format quantity with unit
export const formatQuantityWithUnit = (quantity: number, unitCode: string): string => {
  const unit = getUnitCodeInfo(unitCode);
  const symbol = unit?.symbol || unitCode;
  
  // Handle different number formatting based on unit type
  let formattedQuantity: string;
  if (quantity % 1 === 0) {
    // Integer
    formattedQuantity = quantity.toString();
  } else {
    // Decimal - use appropriate precision
    formattedQuantity = quantity.toFixed(2).replace(/\.?0+$/, '');
  }
  
  return `${formattedQuantity} ${symbol}`;
};

export type CommonUnitCode = typeof COMMON_UNIT_CODES[number];
export type AllUnitCode = keyof typeof UNIT_CODE_DATA;
export type UnitCategory = keyof typeof UNIT_CATEGORIES;